import { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Layout from '@components/global/Layout';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import type {
  GetStaticPropsContext,
  GetStaticPathsResult,
  GetStaticPropsResult,
} from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import { MEC_TITLE } from '@utils/constants';
import getCategoryPageByUid from '@adapters/prismic/fetching/categoryPage/getCategoryPageByUid';
import CategoryPageSliceZone from '@components/cms/CategoryPageSliceZone';
import { handlePromoDetailsTracking } from '@utils/gtm';
import {
  AlgonomyPlacement,
  RecommendationsLoader,
} from '@components/product/ProductCollectionComponent';
import { CategoryPageDocumentDataBodyBestSellerRecommendationsSlicePrimary } from 'prismicio-types';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

interface CategoryPageProps {
  categoryPage: any;
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[];
  xDefault: string;
  stores: MyStoreData[] | null;
  recommendationsCategory: AlgonomyPlacement | null;
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>): Promise<
  GetStaticPropsResult<CategoryPageProps>
> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!params?.slug) {
    throw new Error(`Branded Store page has no slug`);
  }

  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const pageLocale = locale ?? defaultLocale;
  const previewRef = getPreviewRef(preview, previewData);

  const categoryPage = await getCategoryPageByUid(
    params.slug,
    pageLocale,
    previewRef
  );

  if (!categoryPage) {
    throw new Error(`Category page with slug '${params?.slug}' not found`);
  }

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const canonicalUrl = localePathAbs(locale, `/category/${params.slug}`);
  const langAltUrl = localeAltPathAbs(locale, `/category/${params.slug}`);
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  const recommendationsCategory =
    (
      categoryPage.body.find(
        (slice: { slice_type: string }) =>
          slice.slice_type === 'best_seller_recommendations'
      )
        ?.primary as CategoryPageDocumentDataBodyBestSellerRecommendationsSlicePrimary
    )?.category ?? null;

  return {
    props: {
      stores,
      categoryPage,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      bannerStripData,
      recommendationsCategory,
      promoBarData,
    },
  };
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function CategoryPageComponent({
  categoryPage,
  canonicalUrl,
  langAltUrl,
  xDefault,
  recommendationsCategory,
}: CategoryPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { meta_title: metaTitle, meta_description: metaDescription } =
    categoryPage;

  const [categoryPageRecommendations, setCategoryPageRecommendations] =
    useState<AlgonomyPlacement[]>([]);

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  return (
    <>
      <NextSeo
        description={metaDescription}
        title={`${metaTitle} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          locale,
        }}
      />

      <RecommendationsLoader
        pageType="categoryLandingPage"
        data={{
          category: recommendationsCategory,
        }}
        onLoad={(data) => {
          setCategoryPageRecommendations(data);
        }}
        trigger={recommendationsCategory !== undefined}
      />

      <CategoryPageSliceZone
        slices={categoryPage?.body}
        categoryPageRecommendations={categoryPageRecommendations}
      />
    </>
  );
}

CategoryPageComponent.Layout = Layout;
